// Heatmap overlay — severity-weighted kernel density. Used at low zooms
// (z < 5) and as the auto-collapse fallback when deck.gl FPS dips.

import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import type { intel as i } from '@eidollon/contracts';
import { getMapPalette } from '../colors.js';
import { severityWeight, withGeom } from './_shared.js';
import type { LayerSpec } from './registry.js';

// deck.gl HeatmapLayer mutates the `colorRange` array internally, so we
// hand it a fresh tuple array on each layer build (cheap — six elements).
function heatmapColorRange(): [number, number, number, number][] {
  const s = getMapPalette().heatmapStops;
  return [[...s.transparent], [...s.low], [...s.info], [...s.warn], [...s.high], [...s.hot]];
}

export const heatmapLayer: LayerSpec = {
  id: 'heatmap',
  label: 'HMP · heatmap',
  kind: 'heatmap',
  signalTypes: [],
  color: 'var(--amber)',
  colorAxis: 'severity',
  description: 'Severity-weighted kernel density (low zoom only)',
  classification: 'OPEN',
  legend: {
    swatch: 'var(--amber)',
    label: 'HMP',
    hint: 'Severity-weighted kernel density',
  },
  // Default OFF (T-flicker): deck.gl 9.0.x HeatmapLayer emits a malformed
  // screen-pass shader pipeline (`Module picking not found` / `Unknown
  // binding project32Uniforms` on intel-heatmap-triangle-layer-cached).
  // Benign on most GPUs, but a prime suspect for the constant whole-canvas
  // repaint/flicker reported on other hardware. The layer still constructs
  // and can be re-enabled from the legend; this just keeps the broken
  // pipeline out of the default render path while we evaluate a replacement
  // (density-hex already covers the low-zoom aggregate view).
  defaultEnabled: false,
  select: (rows) => withGeom(rows.slice()),
  visibleAtZoom: (z) => z < 5,
  deckLayer: ({ rows }) => {
    const data = withGeom(rows);
    return new HeatmapLayer<i.IntelSignal>({
      id: 'intel-heatmap',
      data,
      pickable: false,
      // biome-ignore lint/style/noNonNullAssertion: withGeom() guarantees geom.
      getPosition: (s) => [s.geom!.lng, s.geom!.lat],
      getWeight: (s) => severityWeight(s.severity),
      aggregation: 'SUM',
      radiusPixels: 36,
      intensity: 1,
      threshold: 0.04,
      colorRange: heatmapColorRange(),
    });
  },
};
